import { Box, Input, Text } from '@zillow/constellation-10';
import { IconChevronDownFilled } from '@zillow/constellation-icons';
import { List, type RowComponentProps } from 'react-window';
import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@/styled-system/jsx';

type SystemStyleObject = React.ComponentProps<typeof Box>['css'];

export type CustomComboboxOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type CustomComboboxProps = {
  options: Array<CustomComboboxOption>;
  value?: string;
  onChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  css?: SystemStyleObject;
  renderInput?: (props: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
  }) => React.ReactNode;
  renderClear?: () => React.ReactNode;
  itemHeight?: number;
  maxDropdownHeight?: number;
};

const ITEM_HEIGHT = 48;
const MAX_DROPDOWN_HEIGHT = 300;

const StyledOptionRow = styled(Box, {
  base: {
    display: 'flex',
    alignItems: 'center',
    paddingBlock: '200',
    paddingInline: '400',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: 'text.neutral',
    fontWeight: 'normal',
    _hover: { backgroundColor: 'Combobox.Option.bg.hover' },
  },
  variants: {
    disabled: {
      true: {
        cursor: 'default',
        color: 'text.disabled',
        _hover: {},
      },
    },
    selected: {
      true: {
        '& span': {
          fontWeight: 'bold',
        },
        backgroundColor: 'Combobox.Option.bg.hover',
      },
    },
  },
});

const StyledTrigger = styled(Box, {
  base: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'border.input',
    borderRadius: '200',
    backgroundColor: 'surface.input',
    height: 'max-content',
    paddingRight: 'xs',
    cursor: 'pointer',
    _hover: { borderColor: 'border.active' },
    '& input': {
      border: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: 'transparent !important',
    },
  },
  variants: {
    open: {
      true: {
        borderColor: 'border.active',
      },
    },
  },
});

type OptionRowProps = {
  options: Array<CustomComboboxOption>;
  activeIndex: number;
  selectedValue: string | undefined;
  onSelect: (value: string) => void;
  onHover: (index: number) => void;
};

const OptionRow = ({ index, style, options, selectedValue, onSelect, onHover }: RowComponentProps<OptionRowProps>) => {
  const option = options[index];
  const isSelected = option.value === selectedValue;
  const isDisabled = option.disabled;

  return (
    <StyledOptionRow
      style={style}
      disabled={isDisabled}
      selected={isSelected}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault();
        if (!isDisabled) onSelect(option.value);
      }}
      onMouseEnter={() => onHover(index)}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
    >
      <Text
        css={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {option.label}
      </Text>
    </StyledOptionRow>
  );
};

export const CustomCombobox = ({
  options,
  value,
  onChange,
  open: controlledOpen,
  onOpenChange,
  placeholder,
  css: cssProp = {},
  renderInput,
  renderClear,
  itemHeight = ITEM_HEIGHT,
  maxDropdownHeight = MAX_DROPDOWN_HEIGHT,
}: CustomComboboxProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isOpen = controlledOpen ?? internalOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (onOpenChange) {
        onOpenChange(nextOpen);
      } else {
        setInternalOpen(nextOpen);
      }
      if (!nextOpen) setActiveIndex(-1);
    },
    [onOpenChange],
  );

  const listHeight = Math.min(options.length * itemHeight, maxDropdownHeight);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onChange?.(selectedValue);
      setOpen(false);
    },
    [onChange, setOpen],
  );

  const scrollToIndex = useCallback(
    (index: number) => {
      const scrollContainer =
        listRef.current?.querySelector('[data-react-window-list]') ?? listRef.current?.firstElementChild;
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;
      const itemTop = index * itemHeight;
      const itemBottom = itemTop + itemHeight;
      const visibleTop = scrollTop;
      const visibleBottom = scrollTop + maxDropdownHeight;

      if (itemTop < visibleTop) {
        scrollContainer.scrollTop = itemTop;
      } else if (itemBottom > visibleBottom) {
        scrollContainer.scrollTop = itemBottom - maxDropdownHeight;
      }
    },
    [itemHeight, maxDropdownHeight],
  );

  const handleFocus = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    setOpen(true);
  }, [setOpen]);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  }, [setOpen]);

  useEffect(() => {
    return () => clearTimeout(blurTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setOpen]);

  const displayValue = value ?? '';

  return (
    <Box ref={containerRef} css={{ position: 'relative', ...cssProp }}>
      <StyledTrigger open={isOpen} onClick={handleFocus}>
        <Box css={{ flex: 1, overflow: 'hidden' }}>
          {renderInput ? (
            renderInput({
              value: displayValue,
              onChange: () => {},
              onFocus: handleFocus,
              onBlur: handleBlur,
            })
          ) : (
            <Input value={displayValue} readOnly placeholder={placeholder} onFocus={handleFocus} onBlur={handleBlur} />
          )}
        </Box>
        {renderClear ? renderClear() : null}
        <Box
          css={{ display: 'flex', alignItems: 'center', color: 'text.subtle', flexShrink: 0, marginInlineEnd: '200' }}
        >
          <IconChevronDownFilled
            style={{
              width: 16,
              height: 16,
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      </StyledTrigger>

      {isOpen && options.length > 0 && (
        <Box
          ref={listRef}
          css={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: '2px',
            border: 'none',
            borderRadius: '200',
            backgroundColor: 'white',
            boxShadow: 'Combobox.Content',
            overflow: 'hidden',
          }}
          role="listbox"
        >
          <List
            rowComponent={OptionRow}
            rowCount={options.length}
            rowHeight={itemHeight}
            rowProps={{
              options,
              activeIndex,
              selectedValue: value,
              onSelect: handleSelect,
              onHover: setActiveIndex,
            }}
            style={{ height: listHeight }}
          />
        </Box>
      )}
    </Box>
  );
};
